import { KatalComponent, register, nextUniqueId } from '../../shared/base';
import { has } from '../../shared/utils';
import getString from './get-string';

const SELECTION_METHODS_TO_PROXY = [
  'isRowSelected',
  'isAllSelected',
  'getSelectedRows',
  'setSelectedRows',
  'selectRow',
  'unselectRow',
  'selectAll',
  'unselectAll',
];
const SELECTION_METHOD_ERROR =
  "To enable this method set config option 'enableSelectionColumn' to true";
const HEADER_INNER_ELEMENT = 'kat-table-cell-inner';
const SORTING_CONTAINING_CLASS = 'kat-table-sorting-controls';
const SORT_ASCENDING = 'asc';
const SORT_DESCENDING = 'desc';

/**
 * @component {kat-data-table} KatalDataTable Component allows user to have a styled table. This table is designed for more complex data with special formatting needs or a way to display a large dataset.
 * @event rowDataChanged This event is fired whenever the data table receives new row data.
 * @event selectRow This event is fired whenever a row selection checkbox is checked.
 * @event unselectRow This event is fired whenever a row selection checkbox is unchecked.
 * @event selectAll This event is fired whenever the selectAll checkbox is checked.
 * @event unselectAll This event is fired whenever the selectAll checkbox is unchecked.
 * @event selectionChanged This event is fired anytime the list of selected rows changes.
 * @event sortColumnChanged This event is fired anytime a column is sorted by a user.
 * @event settingsChanged This event is fired anytime settings which determine the rows of the table are changed. This currently includes: sorting
 * @theme flo
 * @example SimpleTable { "title-label": "My Table", "columns": [{ "label": "Order", "property": "order-num" }, { "label": "Price", "property": "cost-amount" }, { "label": "Date", "property": "date-str" }], "rowData": [{ "order-num": "ord-123-abc-xyz", "cost-amount": 1000, "date-str": "June 2002" }, { "order-num": "ord-456-abc-123", "cost-amount": 456, "date-str": "June 2016" }] }
 * @status Production
 * @ignore
 */
/**
 * The <kat-data-table> component. A complex data-driven table. Currently supports row
 * formatting/styling, column formatting/styling, custom header formatting, custom cell formatting,
 * selecting rows through a checkbox columns, and sorting controls.
 */
@register('kat-data-table')
export class KatDataTable extends KatalComponent {
  static get observedAttributes() {
    return ['title-label'];
  }

  constructor() {
    super([...KatDataTable.observedAttributes, 'rowData', 'columns', 'config']);

    this._ariaTitleId = nextUniqueId();

    this.SORT_ASCENDING = SORT_ASCENDING;
    this.SORT_DESCENDING = SORT_DESCENDING;

    this._needsFullRender = true;
    this._columnFormatters = [];
    this._rowFormatters = [];
    this._columns = [];
    this._rowData = [];

    this._internalColumns = {};

    _resetSortedState(this, false);
    this.addColumnFormatter(_sortedColumnFormatter.bind(null, this));

    this._selection = null;
    // Proxy all the methods for checkbox selection feature to this._selection
    _proxyMethodsThrough(
      this,
      '_selection',
      SELECTION_METHODS_TO_PROXY,
      SELECTION_METHOD_ERROR
    );

    this._rowTemplate = null;

    this._isConnected = false;
    this._wasRenderedBeforeConnected = false;
    this._isStructureReady = false;
  }

  /**
   * Standard function to initialize the web component.
   *
   * @ignore
   */
  connectedCallback() {
    super.connectedCallback();

    this._isConnected = true;
    if (!this._wasRenderedBeforeConnected) {
      this.render();
    }
  }

  /**
   * @classprop {string} title-label Label for the entire table.
   * @ignore
   */
  get titleLabel() {
    return this.getAttribute('title-label') || '';
  }

  set titleLabel(value) {
    this.setAttribute('title-label', value);
  }

  /**
   * A simple object expected to be returned by the header or cell formatters.
   * @typedef {Object} FormatterConfig
   * @property {string} text A simple string to display in the cell or header.
   * @property {object} element An HTMLElement to place inside the cell or header.
   * @property {object} attributes An object of attributes to apply to the cell or header container.
   */
  /**
   * A function used to define how a header is formatted.
   * @callback HeaderFormatter
   * @param {object} lastElement The previous HTML element returned by this function. Recommend to be modified instead of returning a new element.
   * @returns {FormatterConfig}
   */
  /**
   * A function used to define how a cell is formatted.
   * @callback CellFormatter
   * @param {number} rowIndex The index of the row of the cell to format.
   * @param {object} rowData The data of the row of the cell to format.
   * @param {object} lastElement The previous HTML element returned by this function. Recommend to be modified instead of returning a new element.
   * @returns {FormatterConfig}
   */
  /**
   * A column configuration object.
   * @typedef {Object} ColumnConfiguration
   * @type {object}
   * @property {string} label The header label of the column.
   * @property {string} property The property of the rowData object to use to display in this column's cell.
   * @property {CellFormatter} formatter The formatter used to format row cells of the column.
   * @property {HeaderFormatter} headerFormatter The formatter used to format the header of the column.
   * @property {boolean} sortable If true this column is given right-aligned sorting buttons.
   */
  /**
   * @classprop {json} columns An array of objects used to configure the columns of the table.
   * @ignore
   */
  /**
   * @param {ColumnConfiguration[]} columns An array of column configuration objects that represent the columns of the table.
   */
  get columns() {
    return this._columns;
  }

  set columns(columns) {
    if (Array.isArray(columns)) {
      const oldColumnCount = this._columns.length;

      this._columns = columns;

      // Recreate the row template
      if (this._columns.length !== oldColumnCount) {
        _updateRowTemplate(this);
      }
    } else {
      throw new Error('KatalDataTable - unrecognized columns format provided.');
    }

    _resetSortedState(this);
    this._needsFullRender = true;
    this.render();
  }

  /**
   * @classprop {json} rowData An array of objects used to populate the table with data. Each element
   * in the array represents the data of one row.
   */
  get rowData() {
    return this._rowData;
  }

  set rowData(rowData) {
    if (Array.isArray(rowData)) {
      this._rowData = rowData;
      _syncElementsWithData(this);
    } else {
      throw new Error('KatalDataTable - unrecognized rowData format provided.');
    }

    if (this._selection) {
      this._selection.onRowDataChanged();
    }

    this.render();

    this.dispatchEvent(
      new CustomEvent('rowDataChanged', {
        bubbles: true,
        detail: { rowData },
      })
    );
  }

  /**
   * A function used to format a column.
   * @callback ColumnFormatter
   * @param {number} columnIndex The index of the column of the cell to format.
   * @param {ColumnConfiguration} column The configuration object of the column of the cell to format.
   * @returns {object} The attributes to apply to the cell.
   */

  /**
   * Adds a new column formatter. Column formatters are used to assign attributes to header or row
   * cells based on the column they are in.
   *
   * @param {ColumnFormatter} formatter The function called to format the cell. Expected to return an object containing the attributes to apply to the cell.
   * @katalmethod
   */
  addColumnFormatter(formatter) {
    this._columnFormatters.push(formatter);
    this.updateAll();
  }

  /**
   * Call to remove an existing columnFormatter. The original function passed to addColumnFormatter is
   * required to remove it. If the formatter is not found nothing will happen.
   *
   * @param {ColumnFormatter} formatter The formatter function passed to addColumnFormatter.
   * @katalmethod
   */
  removeColumnFormatter(formatter) {
    const index = this._columnFormatters.indexOf(formatter);
    if (index > -1) {
      this._columnFormatters.splice(index, 1);
      this.updateAll();
    }
  }

  /**
   * A function used to format a row.
   * @callback RowFormatter
   * @param {number} rowIndex The index of the row to be formatted.
   * @param {object} rowData The data of the row to be formatted.
   * @returns {object} The attributes to apply to the row.
   */
  /**
   * Adds a row formatter to the list of formatters used to dynamically assign attributes to rows.
   * The formatter accepts the index and data of a given row and returns attributes to assign to
   * the row element.
   *
   * @param {RowFormatter} formatter The function called to format the row. Expected to return an object containing the attributes to apply to the row.
   * @katalmethod
   */
  addRowFormatter(formatter) {
    this._rowFormatters.push(formatter);
    this.updateRows();
  }

  /**
   * Call to remove an existing rowFormatter. The original function passed to addRowFormatter is
   * required to remove it. If the formatter is not found nothing will happen.
   *
   * @param {RowFormatter} formatter The formatter function passed to addRowFormatter.
   * @katalmethod
   */
  removeRowFormatter(formatter) {
    const index = this._rowFormatters.indexOf(formatter);
    if (index > -1) {
      this._rowFormatters.splice(index, 1);
      this.updateRows();
    }
  }

  /**
   * @classprop {json} config Additional configuration options to support the more complex features of the table.
   * @ignore
   */
  get config() {
    return this._config;
  }

  /**
   * When config is set complex supporting features are initialized or cleaned up.
   */
  set config(newConfig) {
    this._config = newConfig;

    if (this._config?.enableSelectionColumn && !this._selection) {
      this._selection = new KatalTableSelection(this);
      this._internalColumns.selectionColumn = this._selection.createCheckboxColumn();

      _updateRowTemplate(this);
      this._needsFullRender = true;
    } else if (!this._config?.enableSelectionColumn && this._selection) {
      this._selection.cleanUp();
      this._selection = null;
      delete this._internalColumns.selectionColumn;

      _updateRowTemplate(this);
      this._needsFullRender = true;
    }

    this.render();
  }

  /**
   * A simple object describing the sorted column and direction.
   * @typedef {Object} SortState
   * @property {ColumnConfiguration} column The column configuration of the column currently sorted.
   * @property {string} direction The sort direction, either "asc" or "desc".
   */
  /**
   * Returns the current sort state.
   *
   * @returns {SortState} The sorted column and direction.
   * @katalmethod
   */
  getSortDirection() {
    return {
      column: this._sortedState.column,
      direction: this._sortedState.direction,
    };
  }

  /**
   * Attempts to sort the given column in the given direction. A column must be configured as
   * sortable in its column configuration first.
   *
   * @param {number} columnIndex The index of the column to sort.
   * @param {string} sortDirection The direction to sort the column. Either "asc" for ascending or "desc" for descending.
   * @katalmethod
   */
  setSortDirection(columnIndex, sortDirection) {
    if (sortDirection !== SORT_ASCENDING && sortDirection !== SORT_DESCENDING) {
      throw new Error(
        `KatalDataTable:setSortDirection - sortDirection must either be "asc" or "desc"`
      );
    }

    const column = this._columns[columnIndex];
    if (!column) {
      throw new Error(
        `KatalDataTable:setSortDirection - No column at index $columnIndex`
      );
    }

    if (!column.sortable) {
      throw new Error(
        `KatalDataTable:setSortDirection - Column at index $columnIndex is not sortable according to column configuration`
      );
    }

    this._sortedState.column = column;
    this._sortedState.direction = sortDirection;

    const columnLabel = _getColumnLabel(columnIndex, column);
    const direction = _getSortDirectionString(sortDirection);
    _ariaCustomUpdateMessage(
      this,
      getString('columnSortedMessage', { columnLabel, direction })
    );

    this.deferredUpdateWrapper(() => {
      this.updateAll();
      _dispatchSortChanged(this);
    });
  }

  /**
   * Disables the current sort state and rerenders the table.
   * @katalmethod
   */
  removeSort() {
    this.deferredUpdateWrapper(() => {
      _resetSortedState(this);
      this.updateAll();
    });
  }

  /**
   * Called to attempt to render the table in full. The structure will only get recreated if
   * needed.
   *
   * @ignore
   */
  render() {
    if (this._isConnected) {
      _performRender(this);
    } else {
      this._wasRenderedBeforeConnected = false;
      // Wraps the actual render logic to prevent to rendering multiple times when the table isn't
      // even attached to the DOM yet. Allows users to call several setters/methods during
      // initialization and be guarenteed to do a minimal amount of renders.
      if (!this._backgroundRenderTimeout) {
        this._backgroundRenderTimeout = setTimeout(() => {
          this._backgroundRenderTimeout = null;
          if (!this._isConnected) {
            _performRender(this);
          }
        }, 300);
      }
    }
  }

  /**
   * Updates the header and all rows using the current rowData set. This will re-run all formatters but won't reset any internal state such as selected rows.
   * Setting rowData will call this internally but it would necessary to call this (or invoke the rowData setter) if you modify rowData in-place.
   *
   * @param {boolean} updateRowElements A flag used to indicate if the row element should be formatted.
   * @param {boolean|number[]|ColumnConfiguration[]} updateRowCells A flag used to indicate if the cells of the row should be updated. An array can be passed instead to permit individual columns to be formatted. An array of integers would be used to permit columns by index or an array of column config objects could be used.
   * @katalmethod
   */
  updateAll(updateRowElements = true, updateRowCells = true) {
    if (!this._isStructureReady) {
      return;
    }
    if (this._updatesDeferred) {
      this._updateAllDeferred = true;
      return;
    }

    _syncElementsWithData(this);
    this.updateHeader();
    this.updateRows(updateRowElements, updateRowCells);
  }

  /**
   * Updates all rows using the current rowData set. This will re-run all formatters but won't reset any internal state such as selected rows.  Setting rowData
   * will call this internally but it would necessary to call this (or invoke the rowData setter) if you modify rowData in-place.
   *
   * @param {boolean} updateRowElements A flag used to indicate if the row element should be formatted.
   * @param {boolean|number[]|ColumnConfiguration[]} updateRowCells A flag used to indicate if the cells of the row should be updated. An array can be passed instead to permit individual columns to be formatted. An array of integers would be used to permit columns by index or an array of column config objects could be used.
   * @katalmethod
   */
  updateRows(updateRowElements = true, updateRowCells = true) {
    if (!this._isStructureReady) {
      return;
    }
    if (this._updatesDeferred) {
      this._updateRowsDeferred = true;
      return;
    }

    if (!this._rowData) {
      return;
    }

    for (let i = 0; i < this._rowData.length; i++) {
      this.updateRow(i, updateRowElements, updateRowCells);
    }
  }

  /**
   * Updates the header using the current column configuration. This will re-run all formatters but won't reset any internal state such as selected rows.
   *
   * @katalmethod
   */
  updateHeader() {
    if (!this._isStructureReady) {
      return;
    }
    if (this._updatesDeferred) {
      this._updateHeadersDeferred = true;
      return;
    }

    const columns = _getFullColumns(this);
    if (columns.length === 0) {
      return;
    }

    const headers = this.querySelector('thead > tr').children;
    columns.forEach((column, columnIndex) => {
      if (headers.length - 1 < columnIndex) {
        return;
      }

      const { label, headerFormatter } = column;
      const header = headers[columnIndex];
      const textContainer = header.querySelector(
        HEADER_INNER_ELEMENT + ' .kat-table-header-text'
      );

      _applyFormatterToCell(
        this,
        header,
        textContainer,
        columnIndex,
        column,
        label,
        headerFormatter
      );

      // Must be after _applyFormatterToCell as a formatter is capable of destroying the sorting controls.
      _createOrUpdateSortControls(this, header, columnIndex, column);
    });
  }

  /**
   * Updates the specific row using the current rowData set. This will re-run all formatters but won't reset any internal state such as selected rows.
   *
   * @param {number} rowIndex The index of the row to update.
   * @param {boolean} updateRow A flag used to indicate if the row element should be formatted.
   * @param {boolean|number[]|ColumnConfiguration[]} updateCells A flag used to indicate if the cells of the row should be updated. An array can be passed instead to permit individual columns to be formatted. An array of integers would be used to permit columns by index or an array of column config objects could be used.
   * @katalmethod
   */
  updateRow(rowIndex, updateRow = true, updateCells = true) {
    if (!this._isStructureReady) {
      return;
    }
    const columns = _getFullColumns(this);
    const { _rowData, _rowFormatters } = this;
    const rowElements = this.querySelector('tbody').children;

    if (
      rowIndex < 0 ||
      rowElements.length - 1 < rowIndex ||
      _rowData.length - 1 < rowIndex
    ) {
      return;
    }

    const rowElement = rowElements[rowIndex];
    const thisRowData = _rowData[rowIndex];
    if (updateRow) {
      const attributes = _applyFormatters(
        _rowFormatters,
        rowIndex,
        thisRowData
      );
      _applyAttributesToElement(rowElement, attributes);
    }

    if (updateCells) {
      columns.forEach((column, columnIndex) => {
        const { property, formatter } = column;

        // updateCells could be an array that tells the table which columns need to be
        // updated. It can contain column indexes or column configuration objects.
        if (Array.isArray(updateCells)) {
          if (
            updateCells.indexOf(columnIndex) === -1 &&
            updateCells.indexOf(column) === -1
          ) {
            return;
          }
        }

        const cells = rowElement.children;
        if (cells.length - 1 < columnIndex) {
          return;
        }

        const cell = cells[columnIndex];
        const simpleFormatter = formatter
          ? formatter.bind(null, rowIndex, thisRowData)
          : null;
        _applyFormatterToCell(
          this,
          cell,
          cell,
          columnIndex,
          column,
          thisRowData[property],
          simpleFormatter
        );
      });
    }
  }

  /**
   * Activates the deferred updating and marks that an updateAll is needed, calls the providing
   * function, and then commits deferred updates. An update will happen even if an exception was
   * thrown in the function passed.
   *
   * @param {function} callback A function called while updates are deferred. Modifications to the
   * table inside this function will cause the smallest amount of updates.
   * @katalmethod
   */
  deferredUpdateWrapper(callback) {
    if (typeof callback !== 'function') {
      const type = typeof callback;
      throw new Error(
        `KatalDataTable:deferredUpdateWrapper - expected "function" but callback param was "${type}"`
      );
    }

    this._updatesDeferred = true;

    try {
      callback();
    } finally {
      _updateDeferred(this);
    }
  }

  /**
   * Specialized method for doing an optimized update of the selection column.
   *
   * @ignore
   */
  updateForMassSelectionChange() {
    this.updateAll(true, [this._internalColumns.selectionColumn]);
  }

  /**
   * Responds to attribute changes and is used to quickly update relevant parts of the DOM without
   * a full render.
   *
   * @ignore
   */
  attributeChangedCallback(attr, oldValue, newValue) {
    if (oldValue === null) {
      return;
    }

    if (oldValue !== newValue) {
      if (attr === 'title-label') {
        _updateTitleLabel(this, newValue);
      } else {
        this.render();
      }
    }
  }
}

/**
 * Returns the full list of columns which is combination of internally defined columns and client
 * provided ones. The table controls the exact position of it's internal columns.
 *
 * @ignore
 */
function _getFullColumns(table) {
  const leftColumns = [];
  if (table._internalColumns.selectionColumn) {
    leftColumns.push(table._internalColumns.selectionColumn);
  }
  return leftColumns.concat(table._columns);
}

/**
 * Updates the title label of the table in place.
 *
 * @ignore
 */
function _updateTitleLabel(table, newLabel) {
  const title = table.querySelector('kat-table-title');
  if (title) {
    title.innerText = newLabel;
  }
}

/**
 * Actually performs the render. Either rendering the structure or just updating the headers and
 * rows.
 *
 * @ignore
 */
function _performRender(table) {
  if (table._needsFullRender) {
    _renderStructure(table);
    table._needsFullRender = false;
  }

  if (table._rowData.length > 0 || table._columns.length > 0) {
    table.updateAll();
  }

  table._wasRenderedBeforeConnected = true;
}

/**
 * Creates the structure for the entire table but does not fill in the header or rows with data.
 *
 * @ignore
 */
function _renderStructure(table) {
  const { titleLabel } = table;

  table.innerHTML = '';

  const newDOM = document.createDocumentFragment();
  const titleEle = document.createElement('kat-table-title');
  titleEle.setAttribute('id', table._ariaTitleId);
  titleEle.textContent = titleLabel;
  newDOM.appendChild(titleEle);

  const innerTable = document.createElement('table');
  innerTable.setAttribute('aria-labelledby', table._ariaTitleId);
  innerTable.innerHTML = `
        <thead>
            <tr></tr>
        </thead>
    `;

  const body = document.createElement('tbody');
  innerTable.appendChild(body);

  const ariaUpdateMessage = document.createElement('div');
  ariaUpdateMessage.setAttribute(
    'class',
    'kat-aria-update-message kat-visually-hidden'
  );
  ariaUpdateMessage.setAttribute('aria-live', 'polite');

  newDOM.appendChild(innerTable);
  newDOM.appendChild(document.createElement('kat-table-footer'));
  newDOM.appendChild(ariaUpdateMessage);
  table.appendChild(newDOM);

  _populateHeaders(table);
  _populateRows(table);

  table._isStructureReady = true;
}

/**
 * Creates the structure for the header but does not fill in the contents of it's cells.
 *
 * @ignore
 */
function _populateHeaders(table) {
  const columns = _getFullColumns(table);
  const header = table.querySelector('thead tr');

  const columnTemplate = document.createElement('th');
  const inner = document.createElement('kat-table-cell-inner');
  const textContainer = document.createElement('span');
  textContainer.setAttribute('class', 'kat-table-header-text');
  inner.appendChild(textContainer);
  columnTemplate.appendChild(inner);

  const columnNoHeaderTemplate = document.createElement('td');
  columnNoHeaderTemplate.appendChild(inner.cloneNode(true));

  columns.forEach(column => {
    if (column.useHeader === false) {
      header.appendChild(columnNoHeaderTemplate.cloneNode(true));
    } else {
      header.appendChild(columnTemplate.cloneNode(true));
    }
  });
}

/**
 * Creates the structure for all the rows in the table but does not fill in the contents of it's
 * cells.
 *
 * @ignore
 */
function _populateRows(table) {
  if (!table._rowTemplate) {
    return;
  }

  const { _rowData } = table;

  const body = table.querySelector('tbody');
  for (let i = 0; i < _rowData.length; i++) {
    body.appendChild(table._rowTemplate.cloneNode(true));
  }
}

function _syncElementsWithData(table) {
  if (!table._isStructureReady) {
    return;
  }

  const body = table.querySelector('tbody');
  const rowElements = body.children;
  if (rowElements.length !== table._rowData.length) {
    _addOrRemoveRows(table, rowElements.length, table._rowData.length);
  }
}

/**
 * Handles formatting a cell (either header or table row). The formatter is optional but is expected
 * to be a function that returns a config object which contains text, attributes, and/or element.
 * Attributes are applied to the element and element/text is inserted inside it. If element is in
 * the config then text is ignored. The formatter is given the current element found inside the cell
 * which was passed by the formatter in a previous call. The formatter can then manipulate the
 * element in place, by returning the element in the config the table will not reinsert the element
 * into the cell.
 *
 * @ignore
 */
function _applyFormatterToCell(
  table,
  cell,
  innerCell,
  columnIndex,
  column,
  text,
  formatter
) {
  let config = null;
  let lastElement = null;
  if (formatter) {
    lastElement = innerCell.querySelector('*');
    config = formatter(lastElement);
  }
  if (!config) {
    config = { text };
  }
  if (!config.attributes) {
    config.attributes = {};
  }
  // Should probably be after the cell formatter to avoid the formatter from overriding the
  // attributes set in the column formatters.
  const columnFormatterAttributes = _applyFormatters(
    table._columnFormatters,
    columnIndex,
    column
  );

  config.attributes = _mergeAttributes(
    config.attributes,
    columnFormatterAttributes
  );

  // Special case, column headers should always have these accessibility attributes
  if (cell.tagName === 'TH') {
    config.attributes.scope = 'col';
    config.attributes.role = 'columnheader';
  }

  if (config.attributes) {
    _applyAttributesToElement(cell, config.attributes);
  }
  if (config.element) {
    // If formatter returns the same element passed don't touch the DOM.
    if (config.element !== lastElement) {
      innerCell.innerHTML = '';
      innerCell.appendChild(config.element);
    }
  } else {
    innerCell.innerText = _anyToText(config.text);
  }
}

function _anyToText(value) {
  switch (value) {
    case null:
    case undefined:
      return '';
  }

  return '' + value;
}

/**
 * Used to replace an elements attributes with the object passed. Will set attributes that are new
 * or changed and will remove attributes not defined in the new object.
 *
 * @ignore
 */
function _applyAttributesToElement(element, attributes, removeNotFound = true) {
  if (attributes) {
    // Set all attributes in map
    Object.keys(attributes).forEach(attr => {
      if (element.getAttribute(attr) !== attributes[attr]) {
        element.setAttribute(attr, attributes[attr]);
      }
    });

    if (removeNotFound) {
      // Remove all attributes not found in the map
      const elementAttributes = element.attributes;
      // Need to copy attribute names first to avoid modifying attributes while iterating it.
      const currentAttributes = [];
      for (let i = 0; i < elementAttributes.length; ++i) {
        currentAttributes.push(elementAttributes[i].name);
      }

      currentAttributes.forEach(attr => {
        if (!has(attributes, attr)) {
          element.removeAttribute(attr);
        }
      });
    }
  }
}

/**
 * Merges the attributes returned by the given formatters. Returns the merged attributes.
 * @ignore
 */
function _applyFormatters(formatters, index, data) {
  return formatters.reduce((attributes, formatter) => {
    const newAttributes = formatter(index, data);
    return _mergeAttributes(attributes, newAttributes);
  }, {});
}

/**
 * Merges two collections of attributes together. Concatenates classes together
 * into one string.
 * @ignore
 */
function _mergeAttributes(currentAttrs = {}, newAttrs = {}) {
  const mergedAttrs = { ...currentAttrs, ...newAttrs };
  const className = `${currentAttrs.class || ''} ${
    newAttrs.class || ''
  }`.trim();

  if (className) {
    mergedAttrs.class = className;
  }

  return mergedAttrs;
}

/**
 * Recreates the HTML template for a blank row.
 *
 * @ignore
 */
function _updateRowTemplate(table) {
  const columns = _getFullColumns(table);

  table._rowTemplate = document.createElement('tr');
  columns.forEach(() => {
    const element = document.createElement('td');
    table._rowTemplate.appendChild(element);
  });
}

/**
 * Will add rows to the table if new row data contains more rows and will remove rows if new row
 * data contains less rows. This is more effecient than wiping the entire table and recreating the
 * DOM.
 *
 * @ignore
 */
function _addOrRemoveRows(table, currentRowCount, newRowCount) {
  if (!table._rowTemplate) {
    return;
  }

  const body = table.querySelector('tbody');
  if (currentRowCount < newRowCount) {
    for (let i = 0; i < newRowCount - currentRowCount; ++i) {
      body.appendChild(table._rowTemplate.cloneNode(true));
    }
  } else if (currentRowCount > newRowCount) {
    for (let i = 0; i < currentRowCount - newRowCount; ++i) {
      body.removeChild(body.firstChild);
    }
  }
}

/**
 * Sets the sorting state back to it's default value with no column sorted. Triggers a
 * settingsChanged event.
 *
 * @ignore
 */
function _resetSortedState(table, dispatchEvents = true) {
  table._sortedState = {
    column: null,
    direction: null,
  };

  if (dispatchEvents) {
    _dispatchSettingsChanged(table);
  }
}

/**
 * First time called creates the sorting controls element in the column with with sorting enabled.
 * On subsequent calls the icon used to the display the sort direction is updated to the current
 * state. If called when the controls exists but the column was set to not sortable then it's
 * removed.
 *
 * @ignore
 */
function _createOrUpdateSortControls(
  table,
  headerElement,
  columnIndex,
  column
) {
  let sorting = headerElement.querySelector('.' + SORTING_CONTAINING_CLASS);

  if (!column.sortable) {
    if (sorting) {
      sorting.parentNode.removeChild(sorting);
    }
    return;
  }

  let icon = null;
  if (!sorting) {
    sorting = document.createElement('button');
    sorting.classList.add(SORTING_CONTAINING_CLASS);
    icon = document.createElement('kat-icon');
    icon.setAttribute('name', 'sort');
    icon.setAttribute('size', 'small');
    sorting.appendChild(icon);

    sorting.addEventListener(
      'click',
      _sortHandler.bind(null, table, columnIndex, column)
    );

    const inner = headerElement.querySelector(HEADER_INNER_ELEMENT);
    inner.appendChild(sorting);
  } else {
    icon = sorting.querySelector('kat-icon');
  }

  const columnLabel = _getColumnLabel(columnIndex, column);
  if (table._sortedState.column === column) {
    if (table._sortedState.direction === SORT_ASCENDING) {
      icon.setAttribute('name', 'arrow-down');
      sorting.setAttribute(
        'aria-label',
        getString('sortButtonLabelToggle', {
          columnLabel,
          direction: getString('sortDescending'),
        })
      );
      headerElement.setAttribute('aria-sort', 'ascending');
    } else {
      icon.setAttribute('name', 'arrow-up');
      sorting.setAttribute(
        'aria-label',
        getString('sortButtonLabelToggle', {
          columnLabel,
          direction: getString('sortAscending'),
        })
      );
      headerElement.setAttribute('aria-sort', 'descending');
    }
  } else {
    icon.setAttribute('name', 'sort');
    sorting.setAttribute(
      'aria-label',
      getString('sortButtonLabelStart', { columnLabel })
    );
  }
}

/**
 * Handles the click on the sorting button in the header of a column with sorting enabled. Toggles
 * between ascending and descending sorting on each click. Always starts with ascending for a new
 * column.
 *
 * @ignore
 */
function _sortHandler(table, columnIndex, column) {
  if (table._sortedState.column === column) {
    table._sortedState.direction =
      table._sortedState.direction === SORT_ASCENDING
        ? SORT_DESCENDING
        : SORT_ASCENDING;
    table.deferredUpdateWrapper(() => {
      table.updateHeader();
      _dispatchSortChanged(table);
    });
  } else {
    table._sortedState.column = column;
    table._sortedState.direction = SORT_ASCENDING;
    table.deferredUpdateWrapper(() => {
      table.updateAll();
      _dispatchSortChanged(table);
    });
  }

  const columnLabel = _getColumnLabel(columnIndex, column);
  const direction = _getSortDirectionString(table._sortedState.direction);
  _ariaCustomUpdateMessage(
    table,
    getString('columnSortedMessage', { columnLabel, direction })
  );
}

function _getColumnLabel(columnIndex, column) {
  return (
    column.label ||
    getString('defaultColumnLabel', { columnIndex: columnIndex + 1 })
  );
}

function _getSortDirectionString(sortDirection) {
  if (sortDirection === 'asc') {
    return getString('sortAscending');
  }
  if (sortDirection === 'desc') {
    return getString('sortDescending');
  }

  return 'unknown';
}

/**
 * Adds a class to each cell of the current sorted column.
 *
 * @ignore
 */
function _sortedColumnFormatter(table, columnIndex, column) {
  if (column.sortable && table._sortedState.column === column) {
    return { class: 'kat-table-sort-column-highlight' };
  }
}

/**
 * Dispatches the sortColumnChanged event with the current sort state.
 *
 * @ignore
 */
function _dispatchSortChanged(table) {
  const evtData = {
    column: table._sortedState.column,
    sortDirection: table._sortedState.direction,
  };
  table.dispatchEvent(
    new CustomEvent('sortColumnChanged', { bubbles: true, detail: evtData })
  );
  _dispatchSettingsChanged(table);
}

/**
 * Dispatches the settings changed event with all the current settings.
 *
 * @ignore
 */
function _dispatchSettingsChanged(table) {
  const evtData = {
    sortColumn: table._sortedState.column,
    sortDirection: table._sortedState.direction,
  };
  table.dispatchEvent(
    new CustomEvent('settingsChanged', { bubbles: true, detail: evtData })
  );
}

/**
 * Updates the table if updateAll, updateRows, or updateHeader was marked as deferred. If
 * updateAll is marked as deferred then only it will be called since it performs updateRows and
 * updateHeader internally. This is usually to allow the table to request an update then
 * dispatch an event and if a client also requests an update then only one update happens
 * instead of two.
 *
 * @ignore
 */
function _updateDeferred(table) {
  if (!table._updatesDeferred) {
    return;
  }
  table._updatesDeferred = false;

  if (table._updateAllDeferred) {
    table._updateAllDeferred = false;
    // Prevent more specific deferred updates
    table._updateRowsDeferred = false;
    table._updateHeadersDeferred = false;
    table.updateAll();
  }

  if (table._updateRowsDeferred) {
    table._updateRowsDeferred = false;
    table.updateRows();
  }

  if (table._updateHeadersDeferred) {
    table._updateHeadersDeferred = false;
    table.updateHeader();
  }
}

function _ariaCustomUpdateMessage(table, message) {
  const messageContainer = table.querySelector('.kat-aria-update-message');
  messageContainer.innerText = message;

  setTimeout(() => {
    if (messageContainer.textContent === message) {
      messageContainer.innerText = '';
    }
  }, 2000);
}

/**
 * Creates a function on the table instance that will proxy to a given internal instance. Should
 * make it easy to add more features to the table without bloating this file. These functions are
 * intended to be created in the constructor. If the instance that they proxy to has not been
 * created yet then a useful error will be thrown to the client.
 *
 * Ex. table.selectRow(i) actually calls table._selection.selectRow(i)
 *
 * @ignore
 */
function _proxyMethodsThrough(table, proxyProperty, methodNames, errorMessage) {
  methodNames.forEach(method => {
    if (has(table, method)) {
      throw new Error(
        `KatalDataTable - method "${method}" already exists cannot setup proxy`
      );
    }

    const context = { table, proxyProperty, method, errorMessage };
    table[method] = function (...args) {
      if (!this.table[this.proxyProperty]) {
        throw new Error(
          `KataDataTable:${this.method} - this method is not available: ${this.errorMessage}`
        );
      }
      return this.table[this.proxyProperty][this.method].apply(
        table[this.proxyProperty],
        args
      );
    }.bind(context);
  });
}

/**
 * KatalTableSelection encapsulates all the logic behind the checkbox selection feature of the data
 * table. It will maintain the state of which rows are selected and provides an API to manipulate
 * that state. Whenever the table's rows change the selection state is reset. All methods are
 * expected to be proxied through from the table so all clients call these methods as if they were
 * methods of the table.
 */
export class KatalTableSelection {
  constructor(table) {
    this._table = table;
    this._selectedRows = {};
    this._attachedRowFormatter = this._rowFormatter.bind(this);

    this._table.addRowFormatter(this._attachedRowFormatter);
  }

  /**
   * Returns true if the given row index is selected.
   * @param {number} rowIndex The index of the row to check.
   * @returns {boolean}
   * @katalmethod
   */
  isRowSelected(rowIndex) {
    return !!this._selectedRows[rowIndex];
  }

  /**
   * Returns true if all rows in the table are selected.
   * @returns {boolean}
   * @katalmethod
   */
  isAllSelected() {
    // For all to be checked all visible rows must be in the respective
    // state.
    const rowData = this._table.rowData || [];
    return rowData.every(
      (rowData, index) =>
        index in this._selectedRows && this._selectedRows[index]
    );
  }

  /**
   * A simple object containing the index and data of row.
   * @typedef {Object} RowStruct
   * @property {number} index The index of the row.
   * @property {object} data The data of the row.
   */
  /**
   * Returns a list of all selected rows both their indexes and their data.
   * @returns {RowStruct[]}
   * @katalmethod
   */
  getSelectedRows() {
    return Object.keys(this._selectedRows)
      .filter(rowIndex => this._selectedRows[rowIndex])
      .map(rowIndex => ({
        index: rowIndex,
        data: this._table.rowData[rowIndex],
      }));
  }

  /**
   * Sets the selection state to only the list passed. Each row index passed is selected and
   * every other row is unselected.
   * @param {number[]} rowIndices An array of row indexes.
   * @katalmethod
   */
  setSelectedRows(rowIndices) {
    if (!Array.isArray(rowIndices)) {
      throw new Error(
        'KatalDataTable:setSelectedRows - expect rowIndices to be an array'
      );
    }

    this._selectedRows = {};
    rowIndices.forEach(index => {
      this._selectedRows[index] = true;
    });

    this._dispatchSelectedChanged();
  }

  /**
   * Selects the row at the given rowIndex. Dispatches an event for the state change and for the
   * selectRow operation if was done by a click.
   * @param {number} rowIndex
   * @param {boolean} dispatchEvent If true the selectRow event is fired.
   * @katalmethod
   */
  selectRow(rowIndex, dispatchEvent = false) {
    this._selectedRows[rowIndex] = true;

    this._rowUpdate(rowIndex);
    if (dispatchEvent) {
      this._table.dispatchEvent(
        new CustomEvent('selectRow', {
          bubbles: true,
          detail: { rowIndex },
        })
      );
    }
  }

  /**
   * Unselects the row at the given rowIndex. Dispatches an event for the state change and for the
   * unselectRow operation if was done by a click.
   * @param {number} rowIndex
   * @param {boolean} dispatchEvent If true the unselectRow event is fired.
   * @katalmethod
   */
  unselectRow(rowIndex, dispatchEvent = false) {
    this._selectedRows[rowIndex] = false;

    this._rowUpdate(rowIndex);
    if (dispatchEvent) {
      this._table.dispatchEvent(
        new CustomEvent('unselectRow', {
          bubbles: true,
          detail: { rowIndex },
        })
      );
    }
  }

  /**
   * Causes the table to update its header and just the row that was just selected. Dispatches the selection changed
   * event as well.
   * @ignore
   */
  _rowUpdate(rowIndex) {
    this._table.updateRow(rowIndex);

    this._table.deferredUpdateWrapper(() => {
      this._table.updateHeader();
      this._dispatchSelectedChanged();
    });
  }

  /**
   * Selects all rows. Dispatches an event for the state change and for the selectAll operation
   * if was done by a click.
   * @param {boolean} dispatchEvent If true the selectAll event is fired.
   * @katalmethod
   */
  selectAll(dispatchEvent = false) {
    const rowData = this._table.rowData || [];
    for (let i = 0; i < rowData.length; i++) {
      this._selectedRows[i] = true;
    }

    this._tableUpdate();
    if (dispatchEvent) {
      this._table.dispatchEvent(
        new CustomEvent('selectAll', { bubbles: true })
      );
    }
  }

  /**
   * Unselects all rows. Dispatches an event for the state change and for the unselectRow operation
   * if was done by a click.
   * @param {boolean} dispatchEvent If true the unselectAll event is fired.
   * @katalmethod
   */
  unselectAll(dispatchEvent = false) {
    this._selectedRows = {};

    this._tableUpdate();
    if (dispatchEvent) {
      this._table.dispatchEvent(
        new CustomEvent('unselectAll', { bubbles: true })
      );
    }
  }

  /**
   * Causes the table to update its header and all the rows. Dispatches the selection changed
   * event as well.
   * @ignore
   */
  _tableUpdate() {
    // Use special optimized function to update just the row elements and checkbox cells.
    this._table.updateForMassSelectionChange();

    this._dispatchSelectedChanged();
  }

  /**
   * Dispatches the "selectionChanged" event from the table. Provides the list of currently
   * selected rows.
   * @ignore
   */
  _dispatchSelectedChanged() {
    this._table.dispatchEvent(
      new CustomEvent('selectionChanged', {
        bubbles: true,
        detail: { selectedRows: this.getSelectedRows() },
      })
    );
  }

  /**
   * Creates a new column configuration object for the checkbox column. Contains the header and
   * cell formatters. They create checkbox elements and attach event handlers to them. Their state
   * is managed by making sure the table updates and rerenders the formatters.
   *
   * @returns {ColumnConfiguration} A column configuration for the checkbox column.
   */
  createCheckboxColumn() {
    return {
      headerFormatter: this._headerFormatter.bind(this),
      formatter: this._cellFormatter.bind(this),
      useHeader: false,
    };
  }

  /**
   * Expected to be called by the table each time the underlying row data of the table is changed.
   * Used to reset the selection state.
   */
  onRowDataChanged() {
    this._selectedRows = {};
  }

  /**
   * The formatter used to create the checkbox in the header that will select or unselect all
   * rows.
   * @ignore
   */
  _headerFormatter() {
    const element = document.createElement('kat-checkbox');
    let checked = false;
    let indeterminate = false;

    let allUnchecked = true;
    let allChecked = true;
    // For all to be checked or unchecked all visible rows must be in the respective
    // state.
    const rowData = this._table.rowData || [];
    for (let i = 0; i < rowData.length; i++) {
      if (i in this._selectedRows && this._selectedRows[i]) {
        allUnchecked = false;
      } else {
        allChecked = false;
      }
    }

    if (allUnchecked) {
      checked = false;
    } else if (allChecked) {
      checked = true;
    } else {
      indeterminate = true;
    }
    element.checked = checked;
    element.indeterminate = indeterminate;

    let label = '';
    if (checked) {
      label = getString('selectAllCheckboxAllLabel');
    } else if (indeterminate) {
      label = getString('selectAllCheckboxSomeLabel');
    } else {
      label = getString('selectAllCheckboxNoneLabel');
    }
    element.setAttribute('kat-aria-label', label);

    element.addEventListener('change', evt => {
      if (evt.detail.checked) {
        this.selectAll(true);
      } else {
        this.unselectAll(true);
      }
    });

    return {
      element,
      attributes: { class: 'kat-checkbox-column' },
    };
  }

  /**
   * The formatter used to create the checkboxes for each row.
   *
   * @ignore
   */
  _cellFormatter(rowIndex) {
    const element = document.createElement('kat-checkbox');
    element.checked = !!this._selectedRows[rowIndex];

    const label = getString('rowCheckboxLabel', {
      rowIndex: rowIndex + 1,
    });
    element.setAttribute('kat-aria-label', label);
    element.addEventListener(
      'change',
      this._checkBoxHandler.bind(this, rowIndex)
    );

    return {
      element,
      attributes: { class: 'kat-checkbox-column' },
    };
  }

  /**
   * The formatter used to apply the selected highlighting to each selected row.
   * @ignore
   */
  _rowFormatter(rowIndex) {
    const attributes = {};
    if (this._selectedRows[rowIndex]) {
      attributes['class'] = 'kat-data-table-selected-row';
    }
    return attributes;
  }

  /**
   * The click handler for checkboxes in the table rows.
   * @ignore
   */
  _checkBoxHandler(rowIndex, evt) {
    if (evt.detail.checked) {
      this.selectRow(rowIndex, true);
    } else {
      this.unselectRow(rowIndex, true);
    }
  }

  /**
   * Expected to be called by the table if the selection feature needs to be disabled. Removes the
   * row formatter attached in the constructor.
   */
  cleanUp() {
    this._table.removeRowFormatter(this._attachedRowFormatter);
  }
}
